import React from 'react'
import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import CategoryHeaderBase, * as CategoryHeader from '@segment/matcha/recipes/CategoryHeader'
import CardsGrid from '@segment/matcha/recipes/CardsGrid'

import type { FetchedCatalogIntegration } from '@segment/api/routes/catalog/queries'

import type { Hit } from '@segment/api/algolia/client'

import CatalogGridBase, * as CatalogGrid from '../CatalogGrid'
import IntegrationCardTemplate from '../../../components/IntegrationCard'

import EmptyResults from './EmptyResults'

interface GroupedGridProps {
  query: string
  category?: { name: string; slug: string }
  onCategoryChange: (slug: string) => void
  isEmpty?: boolean
  flattenedPagesCategorySearch?: Hit<FetchedCatalogIntegration>[]
  flattenedPagesAllCategoriesSearch?: Hit<FetchedCatalogIntegration>[]
  nextPageTrigger: JSX.Element
  emptyHits?: number
  isLoading?: boolean
}

const SearchGrid: FC<PropsWithChildren<GroupedGridProps>> = ({
  query,
  category,
  onCategoryChange,
  isEmpty,
  nextPageTrigger,
  flattenedPagesCategorySearch,
  flattenedPagesAllCategoriesSearch,
  emptyHits,
  isLoading,
}) => {
  const title = category
    ? `Search results for "${query}" in ${category.name} category`
    : `Search results for "${query}"`

  return (
    <NarrowCatalogGridBase aria-live="polite" aria-busy={isLoading}>
      <CatalogGrid.Group>
        <CategoryHeaderBase>
          <CategoryHeader.Title variant="h4">{title}</CategoryHeader.Title>
        </CategoryHeaderBase>

        {!isEmpty && (
          <>
            <CardsGrid limitItems={false}>
              {flattenedPagesCategorySearch?.map((integration) => (
                <IntegrationCardTemplate
                  key={integration.objectID}
                  integration={integration}
                  isInCategoryGrid={Boolean(category)}
                  query={query}
                />
              ))}
            </CardsGrid>
            {nextPageTrigger}
          </>
        )}
      </CatalogGrid.Group>
      {isEmpty && (
        <EmptyResults
          onCategoryChange={onCategoryChange}
          query={query}
          entries={flattenedPagesAllCategoriesSearch}
          hitsAmount={emptyHits}
        />
      )}
    </NarrowCatalogGridBase>
  )
}

export default SearchGrid

const NarrowCatalogGridBase = styled(CatalogGridBase)`
  row-gap: 16px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    row-gap: 48px;
  }
`
